import React from 'react';
import { Accordion, Anchor, Box, Button, Container, Divider, Group, Stack, Text, Title } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import Layout from '../layouts/Layout';
import Seo from '../components/Seo';
import landingGroups from '../content/landingPaths.json';
import { LandingCopy, LandingId, landingCopy } from '../content/landingCopy';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../utils/localeNav';
import { PUBLIC_URL } from '../utils/const';
import { SITE_ORIGIN, Locale, pathLocale } from '../utils/seo';
import { redirectWithParameters } from '../utils/common';
import { trackCtaClick } from '../utils/analytics';

const ids = landingGroups.map((group) => group.id);

function isLandingId(id: string): id is LandingId {
    return ids.includes(id as LandingId);
}

function copyFor(lang: Locale, id: LandingId): LandingCopy {
    return landingCopy[lang][id] ?? landingCopy.en[id];
}

const InnerSyncLanding: React.FC<{ id: string }> = ({ id }) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const href = useLocalizedPath();
    const lang = pathLocale(pathname) ?? 'en';

    if (!isLandingId(id)) {
        return null;
    }

    const group = landingGroups.find((item) => item.id === id)!;
    const paths = group.paths as Record<Locale, string>;
    const copy = copyFor(lang, id);
    const canonical = `${SITE_ORIGIN}${paths[lang]}/`;
    const alternates = [
        ...(['en', 'fr', 'es', 'it'] as Locale[]).map((locale) => ({
            hrefLang: locale,
            href: `${SITE_ORIGIN}${paths[locale]}/`,
        })),
        { hrefLang: 'x-default', href: `${SITE_ORIGIN}${paths.en}/` },
    ];

    const signup = () => {
        trackCtaClick('landing', id);
        redirectWithParameters(href('/login'), {}, true);
    };

    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebPage',
                name: copy.title,
                description: copy.description,
                url: canonical,
                inLanguage: lang,
                isPartOf: { '@type': 'WebSite', name: 'Calensync', url: `${SITE_ORIGIN}/` },
            },
            {
                '@type': 'FAQPage',
                mainEntity: copy.faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq.q,
                    acceptedAnswer: { '@type': 'Answer', text: faq.a },
                })),
            },
        ],
    };

    const otherLandings = landingGroups.filter((item) => item.id !== id);

    return (
        <>
            <Seo
                title={copy.title}
                description={copy.description}
                path="/"
                canonical={canonical}
                alternates={alternates}
                lang={lang}
                jsonLd={jsonLd}
            />
            <Box className="hero">
                <Container size="md" py="xl">
                    <Stack gap="md" py={{ base: 'sm', md: 'xl' }}>
                        <Title order={1}>{copy.h1}</Title>
                        <Text size="xl">{copy.lede}</Text>
                        <Group>
                            <Button color="primary.4" c="white" size="compact-xl" onClick={signup}>
                                {copy.cta}
                            </Button>
                        </Group>
                        <Text size="sm">{t('common.free_trial')}</Text>
                    </Stack>
                </Container>
            </Box>
            <Container size="md" py="xl">
                <Stack gap="xl">
                    {copy.points.map((point) => (
                        <Stack key={point.title} gap={4}>
                            <Title order={2} fw={500}>{point.title}</Title>
                            <Text size="lg">{point.body}</Text>
                        </Stack>
                    ))}
                    <Divider />
                    <Title order={2} fw={500}>{copy.howTitle}</Title>
                    {copy.steps.map((step, index) => (
                        <Stack key={step.title} gap={4}>
                            <Title order={3} fw={500}>{index + 1}. {step.title}</Title>
                            <Text size="lg">{step.body}</Text>
                        </Stack>
                    ))}
                </Stack>
            </Container>
            <Box bg="primary.0" py="xl">
                <Container size="md">
                    <Title order={2} fw={500} mb="md">F.A.Q.</Title>
                    <Accordion variant="contained">
                        {copy.faqs.map((faq) => (
                            <Accordion.Item key={faq.q} value={faq.q}>
                                <Accordion.Control>{faq.q}</Accordion.Control>
                                <Accordion.Panel>{faq.a}</Accordion.Panel>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </Container>
            </Box>
            <Container size="md" py="xl">
                <Stack gap="xs">
                    <Title order={2} fw={500}>{copy.moreTitle}</Title>
                    {otherLandings.map((item) => {
                        const other = copyFor(lang, item.id as LandingId);
                        return (
                            <Anchor key={item.id} href={`${PUBLIC_URL}${pathsFor(item.id as LandingId, lang)}`}>
                                {other.h1}
                            </Anchor>
                        );
                    })}
                    <Anchor href={href('/blog')}>{t('footer.blog')}</Anchor>
                </Stack>
            </Container>
        </>
    );
};

const SyncLanding: React.FC<{ id: string }> = ({ id }) => {
    return (
        <Layout>
            <InnerSyncLanding id={id} />
        </Layout>
    );
};

function pathsFor(id: LandingId, lang: Locale): string {
    const group = landingGroups.find((item) => item.id === id)!;
    const path = (group.paths as Record<Locale, string>)[lang];
    return path.endsWith('/') ? path : `${path}/`;
}

export default SyncLanding;
